
import {$} from "../util/seq.js";
import {defineHandleButton} from "./buttons.js";
import {whenEnabled} from "./util.js";
import {formatAsDecimalDegrees} from "../util/strings.js";
import {ø} from "../util/objects.js";
import {first} from "../lib/underscore.js";
import {annotate} from "../engine/annotator.js";

export function activeUnits(layer, selected_units) {
    const {unitDescriptors = {}} = layer ?? {};
    const symbol = selected_units?.[layer?.type] ?? first(Object.keys(unitDescriptors));
    return unitDescriptors[symbol];
}

/**
 * @param container
 * @param {Handle} spotlight_coords
 */
export function defineSpotlight(container, spotlight_coords) {
    const coords = container.select("[data-name=spotlight-coords]");

    function updateDOM(value) {
        container.property("hidden", value === undefined);
        coords.text(formatAsDecimalDegrees(value));
    }

    updateDOM(spotlight_coords.get());
    spotlight_coords.on($`change`, updateDOM);

    defineHandleButton(container.selectAll("[data-name=hide-spotlight]"), spotlight_coords, () => undefined);
}

/**
 * @param container
 * @param {Handle} spotlight_coords
 * @param {Handle} selected_units
 * @param {Handle} interpolation_type
 * @param layerAgent
 */
export function defineLayerSpotlight(container, spotlight_coords, selected_units, interpolation_type, layerAgent) {
    const field = container.select("[aria-label]");
    const button = container.select("button");
    const buttonContent = button.select("span");

    function updateDOM() {
        const layer = layerAgent.value();
        const units = activeUnits(layer, selected_units.get());
        const [λ = NaN, φ = NaN] = spotlight_coords.get() ?? [];
        const interpolate = layer?.field?.()[interpolation_type.get()];
        const val = interpolate?.(λ, φ);
        const {formattedVal = "", fullText = ""} = units?.format(val) ?? {};

        container.property("hidden", formattedVal === "" || !units);
        field.attr("aria-label", fullText).text(formattedVal);
        buttonContent.html(units?.html ?? "");
    }

    function updateStore() {
        const layer = layerAgent.value() ?? {};
        const {unitDescriptors = {}} = layer;
        const symbols = Object.keys(unitDescriptors);
        if (symbols.length > 0) {
            selected_units.getAndSet((selected = {}) => {
                const currentSymbol = selected[layer.type] ?? first(symbols);
                const currentIndex = symbols.indexOf(currentSymbol);
                const nextIndex = (currentIndex + 1) % symbols.length;
                const nextSymbol = symbols[nextIndex];
                return ø(selected, {[layer.type]: nextSymbol});
            });
        }
    }

    updateDOM();
    selected_units.on($`change`, updateDOM);
    spotlight_coords.on($`change`, updateDOM);
    layerAgent.on($`update`, updateDOM);
    button.on($`click`, whenEnabled(updateStore));
}

function locationMarkAt(λ, φ) {
    return {
        type: "Feature",
        geometry: {type: "Point", coordinates: [λ, φ]},
        properties: {pointRadius: 7},
    };
}

/**
 * @param svg
 * @param agents
 * @param {Handle} spotlight_coords
 * @param inputController
 */
export function defineSpotlightMarker(svg, agents, spotlight_coords, inputController) {
    const {globeAgent, fieldAgent} = agents;

    function updateDOM() {
        const globe = globeAgent.value();
        const [λ, φ] = spotlight_coords.get() ?? [NaN, NaN];
        if (!globe || λ !== λ || φ !== φ) {
            annotate(svg, "location-mark", null, undefined);
            return;
        }
        annotate(svg, "location-mark", locationMarkAt(λ, φ), globe.projection);
    }

    function updateStore(point, coords) {
        const x = +point[0];
        const y = +point[1];
        const λ = +coords[0];
        const φ = +coords[1];
        const field = fieldAgent.value();
        // UNDONE: the globe/projection should have the isInsideBoundary function instead of the field (because there
        //         are many fields, one per layer, but only one globe/projection.
        if (x === x && y === y && λ === λ && φ === φ && field?.isInsideBoundary(x, y)) {
            spotlight_coords.set([λ, φ]);
        }
    }

    spotlight_coords.on($`change`, updateDOM);
    globeAgent.on($`update`, updateDOM);
    inputController.on($`click`, updateStore);
}
